/**
 * @module public/js/services/callerIdService.js
 * @summary Authenticated client for the seller caller-ID connection flow.
 */

const ROUTES = Object.freeze({
  status: '/twilio/caller-id/status',
  preference: '/twilio/caller-id/preference',
  verify: '/twilio/caller-id/verify',
  disconnect: '/twilio/caller-id/disconnect',
});

/**
 * Manage Topsail-versus-company-phone caller-ID preferences and verification.
 */
export default class CallerIdService {
  /**
   * Create a caller-ID service.
   *
   * @param {{ fetchJson: Function }} apiClient - Authenticated Topsail API client.
   */
  constructor(apiClient) {
    if (!apiClient?.fetchJson) {
      throw new Error('CallerIdService requires an API client');
    }
    this.api = apiClient;
  }

  /**
   * Load privacy-safe caller-ID connection state.
   *
   * @param {{ tenantId: string, uid?: string }} target - Seller context.
   * @returns {Promise<object>} Caller-ID state.
   */
  getStatus(target) {
    return this.api.fetchJson(ROUTES.status, {
      auth: true,
      body: target,
    });
  }

  /**
   * Save Use Topsail Number or Use My Number.
   *
   * @param {{ tenantId: string, uid?: string, preference: 'topsail'|'own' }} target - Preference request.
   * @returns {Promise<object>} Updated caller-ID state.
   */
  setPreference(target) {
    return this.api.fetchJson(ROUTES.preference, {
      auth: true,
      body: target,
    });
  }

  /**
   * Ask Twilio to place the possession-verification call.
   *
   * @param {{ tenantId: string, phoneNumber: string }} target - Seller phone input.
   * @returns {Promise<{ validationCode: string, maskedNumber: string, lastFour?: string, status: string, alreadyVerified?: boolean }>} Verification instructions or an existing verified connection.
   */
  startVerification(target) {
    return this.api.fetchJson(ROUTES.verify, {
      auth: true,
      body: target,
    });
  }

  /**
   * Remove an external caller ID and return the seller to a Topsail number.
   *
   * @param {{ tenantId: string, uid?: string }} target - Seller context.
   * @returns {Promise<object>} Updated caller-ID state.
   */
  disconnect(target) {
    return this.api.fetchJson(ROUTES.disconnect, {
      auth: true,
      body: target,
    });
  }
}

export { ROUTES };
